import React, { ReactElement } from 'react';
import Table from '../../../../components/table/table.component';
import TableBody from '../../../../components/table-body/table-body.component';
import TableRow from '../../../../components/table-row/table-row.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import FileLink from '../../../../components/file-link/file-link.component';
import Hint from '../../../../components/hint/hint.component';
import Button from '../../../../components/button/button.component';
import FlexContainer from '../../../../components/flex-container/flex-container.component';
import FormFieldText from '../../../../components/form-field-text/form-field-text.component';
import FormFieldUpload from '../../../../components/form-field-upload/form-field-upload.component';
import FormFieldPassword from '../../../../components/form-field-password/form-field-password.component';
import FormFieldCheckbox from '../../../../components/form-field-checkbox/form-field-checkbox.component';
import SinglePageTab from '../../../app/common/single-page-tab/single-page-tab.component';
import FormatService from '../../../../services/format-service';
import { FORMAT_LIST_POWER_BI_TEMPLATE } from '../../../../constants/system/format-lists';
import { ACCOUNT_USER_TYPE_SYSTEM_ADMIN } from '../../../../constants/entity/account-user-types';

const SettingsAccountPowerBi: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isRightDrawerOpen,
    formError,
    currentUser,
    accountData,
    isPowerBiTemplateFileDeleted,
    powerBiTemplateFile,
    powerBiTemplateErrorMessage,
    onPowerBiTemplateFileChange,
    onPowerBiTemplateFileDelete,
    onPowerBiTemplateFileError,
    onCancelForm,
    onSubmitForm,
  } = props;

  const formPrefix: string = 'account';
  const isCurrentUserSystemAdmin: boolean = !!currentUser && !!currentUser.accountUserType
    && currentUser.accountUserType === ACCOUNT_USER_TYPE_SYSTEM_ADMIN;

  const powerBiTemplateFileLink: FileItem | undefined = accountData.powerBiTemplateFile ? {
    id: 1,
    name: 'Power BI Template File',
    link: accountData.powerBiTemplateFile,
  } : undefined;

  return (
    <SinglePageTab
      isSaving={isSaving}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isRightDrawerOpen={isRightDrawerOpen}
      formError={formError}
      onCancelForm={onCancelForm}
      onSubmitForm={onSubmitForm}
    >
      <Table>
        <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
          <TableRow>
            <TableCell>
              Power BI Enabled
              {
                !accountData.powerBiIsEnabled && !isCurrentUserSystemAdmin && (
                  <Hint
                    title={'You cannot enable Power BI in your account manually. '
                      + 'Please contact your administrator.'}
                  />
                )
              }
            </TableCell>
            <TableCell>
              {
                (!isEditMode || !isCurrentUserSystemAdmin) && (
                  FormatService.formatBooleanValue(accountData.powerBiIsEnabled)
                )
              }
              {
                isEditMode && isCurrentUserSystemAdmin && (
                  <FormFieldCheckbox
                    withNoOffset
                    withNoLabelOffset
                    name={`${formPrefix}.powerBiIsEnabled`}
                  />
                )
              }
            </TableCell>
          </TableRow>
          {
            accountData.powerBiIsEnabled && accountData.azureIsEnabled
            && (accountData.powerBiTenantId || isEditMode) && (
              <TableRow>
                <TableCell>
                  Power BI Tenant ID
                  {
                    accountData.azureIsEnabled && (
                      <Hint title={'Once Azure is enabled, configuration from this field is in use.'} />
                    )
                  }
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.powerBiTenantId`} />
                    ) : (
                      accountData.powerBiTenantId
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            accountData.powerBiIsEnabled && accountData.azureIsEnabled
            && (accountData.powerBiClientId || isEditMode) && (
              <TableRow>
                <TableCell>
                  Power BI Client ID
                  {
                    accountData.azureIsEnabled && (
                      <Hint title={'Once Azure is enabled, configuration from this field is in use.'} />
                    )
                  }
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.powerBiClientId`} />
                    ) : (
                      accountData.powerBiClientId
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            accountData.powerBiIsEnabled && accountData.azureIsEnabled
            && (accountData.powerBiClientSecret || isEditMode) && (
              <TableRow>
                <TableCell>
                  Power BI Client Secret
                  {
                    accountData.azureIsEnabled && (
                      <Hint title={'Once Azure is enabled, configuration from this field is in use.'} />
                    )
                  }
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldPassword isBordered withNoAutocomplete name={`${formPrefix}.powerBiClientSecret`} />
                    ) : (
                      (!!accountData.powerBiClientSecret
                        && FormatService.formatEncryptedString(accountData.powerBiClientSecret)) || ''
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            accountData.powerBiIsEnabled && (accountData.powerBiGroupId || isEditMode) && (
              <TableRow>
                <TableCell>
                  Power BI Group ID
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.powerBiGroupId`} />
                    ) : (
                      accountData.powerBiGroupId
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            accountData.powerBiIsEnabled && (accountData.powerBiProjectId || isEditMode) && (
              <TableRow>
                <TableCell>
                  Power BI Project ID
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.powerBiProjectId`} />
                    ) : (
                      accountData.powerBiProjectId
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            accountData.powerBiIsEnabled && (accountData.powerBiDatasetId || isEditMode) && (
              <TableRow>
                <TableCell>
                  Power BI Dataset ID
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.powerBiDatasetId`} />
                    ) : (
                      accountData.powerBiDatasetId
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            accountData.powerBiIsEnabled && (accountData.powerBiTemplateFile || isEditMode) && (
              <TableRow>
                <TableCell>
                  Power BI Template File
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FlexContainer isInline>
                        <FormFieldUpload
                          name={`${formPrefix}.powerBiTemplateFile`}
                          buttonLabel="Upload File"
                          error={powerBiTemplateErrorMessage}
                          uploadFile={powerBiTemplateFile}
                          acceptedExtensionList={FORMAT_LIST_POWER_BI_TEMPLATE
                            .map((formatItem: FormatItem): string => formatItem.name)}
                          onFileChange={onPowerBiTemplateFileChange}
                          onError={onPowerBiTemplateFileError}
                        />
                        {
                          !powerBiTemplateFile && !!accountData.powerBiTemplateFile
                          && !isPowerBiTemplateFileDeleted && (
                            <Button isActive isText onClick={onPowerBiTemplateFileDelete}>
                              Delete
                            </Button>
                          )
                        }
                      </FlexContainer>
                    ) : (
                      !!powerBiTemplateFileLink && (
                        <FileLink fileLink={powerBiTemplateFileLink} />
                      )
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
        </TableBody>
      </Table>
    </SinglePageTab>
  );
});

export default SettingsAccountPowerBi;
